import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  PERIODONTAL_SITES,
  getProbingDepthTone,
  type PeriodontalSite,
  type PeriodontalSurface,
} from '../../constants/odontology';
import type {
  PeriodontalMeasurements,
  PeriodontalSiteMeasurement,
  PeriodontalToothMeasurement,
} from '../../types/odontology';

/**
 * Grade de sondagem periodontal de uma arcada: três sítios por superfície
 * (mesial, central, distal), nas faces vestibular e lingual/palatina, mais os
 * achados por dente (mobilidade, furca, ausência).
 *
 * A coluna de rótulos fica fixa e o restante rola na horizontal, já que 16
 * dentes × 3 sítios não cabem em tela de clínica.
 */

const LABEL_WIDTH = 112;
const SITE_WIDTH = 30;

export interface PeriodontalChartGridProps {
  title: string;
  teeth: string[];
  measurements: PeriodontalMeasurements;
  readOnly?: boolean;
  onChange?: (measurements: PeriodontalMeasurements) => void;
}

function getTooth(measurements: PeriodontalMeasurements, tooth: string): PeriodontalToothMeasurement {
  return measurements[tooth] || {};
}

function getSite(
  measurements: PeriodontalMeasurements,
  tooth: string,
  surface: PeriodontalSurface,
  site: PeriodontalSite,
): PeriodontalSiteMeasurement {
  return getTooth(measurements, tooth)[surface]?.[site] || {};
}

const headerCellSx: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: '#F8FAFC',
  borderRight: '1px solid #E2E8F0',
  padding: '4px 8px',
  minWidth: LABEL_WIDTH,
  width: LABEL_WIDTH,
  textAlign: 'left',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
};

const PeriodontalChartGrid: React.FC<PeriodontalChartGridProps> = ({
  title,
  teeth,
  measurements,
  readOnly = false,
  onChange,
}) => {
  const updateSite = (
    tooth: string,
    surface: PeriodontalSurface,
    site: PeriodontalSite,
    patch: Partial<PeriodontalSiteMeasurement>,
  ) => {
    if (readOnly || !onChange) return;
    const toothData = getTooth(measurements, tooth);
    const surfaceData = toothData[surface] || {};
    onChange({
      ...measurements,
      [tooth]: {
        ...toothData,
        [surface]: {
          ...surfaceData,
          [site]: { ...(surfaceData[site] || {}), ...patch },
        },
      },
    });
  };

  const updateTooth = (tooth: string, patch: Partial<PeriodontalToothMeasurement>) => {
    if (readOnly || !onChange) return;
    onChange({ ...measurements, [tooth]: { ...getTooth(measurements, tooth), ...patch } });
  };

  const parseNumber = (raw: string): number | null => {
    const trimmed = raw.trim().replace(',', '.');
    if (!trimmed) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
  };

  const renderNumberRow = (
    label: string,
    surface: PeriodontalSurface,
    field: 'pd' | 'gm',
  ) => (
    <tr>
      <td style={headerCellSx}>{label}</td>
      {teeth.flatMap((tooth) => {
        const absent = getTooth(measurements, tooth).absent;
        return PERIODONTAL_SITES.map((site) => {
          const value = getSite(measurements, tooth, surface, site)[field];
          const isDepth = field === 'pd';
          // A linha de sondagem vira mapa de calor: o fundo da célula marca a
          // faixa clínica, de modo que bolsas profundas saltam à vista sem
          // precisar ler número por número.
          const tone = isDepth ? getProbingDepthTone(value) : null;
          return (
            <td
              key={`${tooth}-${site}`}
              style={{
                padding: 0,
                borderRight: site === 'distal' ? '1px solid #E2E8F0' : '1px solid #F1F5F9',
                borderBottom: '1px solid #F1F5F9',
              }}
            >
              <input
                type="text"
                inputMode="numeric"
                disabled={readOnly || absent}
                value={value ?? ''}
                onChange={(e) => updateSite(tooth, surface, site, { [field]: parseNumber(e.target.value) })}
                aria-label={`${label} dente ${tooth} ${site}${tone && value != null ? ` — ${tone.label}` : ''}`}
                title={tone && value != null ? tone.label : undefined}
                style={{
                  width: SITE_WIDTH,
                  height: 26,
                  border: 'none',
                  outline: 'none',
                  textAlign: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  background: absent ? '#F1F5F9' : tone?.background ?? 'transparent',
                  color: tone ? tone.text : '#0F172A',
                }}
              />
            </td>
          );
        });
      })}
    </tr>
  );

  const renderToggleRow = (
    label: string,
    surface: PeriodontalSurface,
    field: 'bop' | 'plaque',
    activeColor: string,
  ) => (
    <tr>
      <td style={headerCellSx}>{label}</td>
      {teeth.flatMap((tooth) => {
        const absent = getTooth(measurements, tooth).absent;
        return PERIODONTAL_SITES.map((site) => {
          const active = Boolean(getSite(measurements, tooth, surface, site)[field]);
          return (
            <td
              key={`${tooth}-${site}`}
              style={{
                padding: 0,
                borderRight: site === 'distal' ? '1px solid #E2E8F0' : '1px solid #F1F5F9',
                borderBottom: '1px solid #F1F5F9',
              }}
            >
              <button
                type="button"
                disabled={readOnly || absent}
                onClick={() => updateSite(tooth, surface, site, { [field]: !active })}
                aria-label={`${label} dente ${tooth} ${site}`}
                aria-pressed={active}
                style={{
                  width: SITE_WIDTH,
                  height: 20,
                  border: 'none',
                  padding: 0,
                  cursor: readOnly || absent ? 'default' : 'pointer',
                  background: active ? activeColor : absent ? '#F1F5F9' : 'transparent',
                }}
              />
            </td>
          );
        });
      })}
    </tr>
  );

  const renderSelectRow = (
    label: string,
    field: 'mobility' | 'furcation',
  ) => (
    <tr>
      <td style={headerCellSx}>{label}</td>
      {teeth.map((tooth) => {
        const toothData = getTooth(measurements, tooth);
        return (
          <td
            key={tooth}
            colSpan={3}
            style={{ padding: 0, borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #F1F5F9' }}
          >
            <select
              disabled={readOnly || toothData.absent}
              value={toothData[field] ?? ''}
              onChange={(e) => updateTooth(tooth, { [field]: e.target.value === '' ? null : Number(e.target.value) })}
              aria-label={`${label} dente ${tooth}`}
              style={{
                width: SITE_WIDTH * 3,
                height: 24,
                border: 'none',
                outline: 'none',
                textAlign: 'center',
                fontSize: '0.72rem',
                fontWeight: 600,
                background: toothData.absent ? '#F1F5F9' : 'transparent',
                color: Number(toothData[field]) > 0 ? '#B91C1C' : '#0F172A',
              }}
            >
              <option value="">—</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </td>
        );
      })}
    </tr>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#94A3B8',
          mb: 0.75,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content' }}>
          <thead>
            <tr>
              <th style={{ ...headerCellSx, zIndex: 3, borderBottom: '1px solid #E2E8F0' }}>
                Dente
              </th>
              {teeth.map((tooth) => (
                <th
                  key={tooth}
                  colSpan={3}
                  style={{
                    minWidth: SITE_WIDTH * 3,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#0F172A',
                    padding: '4px 0',
                    borderRight: '1px solid #E2E8F0',
                    borderBottom: '1px solid #E2E8F0',
                    background: '#F8FAFC',
                  }}
                >
                  {tooth}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {renderNumberRow('PS vestibular', 'buccal', 'pd')}
            {renderNumberRow('MG vestibular', 'buccal', 'gm')}
            {renderToggleRow('Sangramento V', 'buccal', 'bop', '#DC2626')}
            {renderToggleRow('Placa V', 'buccal', 'plaque', '#60A5FA')}

            {renderNumberRow('PS lingual', 'lingual', 'pd')}
            {renderNumberRow('MG lingual', 'lingual', 'gm')}
            {renderToggleRow('Sangramento L', 'lingual', 'bop', '#DC2626')}
            {renderToggleRow('Placa L', 'lingual', 'plaque', '#60A5FA')}

            {renderSelectRow('Mobilidade', 'mobility')}
            {renderSelectRow('Furca', 'furcation')}

            <tr>
              <td style={headerCellSx}>Ausente</td>
              {teeth.map((tooth) => (
                <td
                  key={tooth}
                  colSpan={3}
                  style={{ textAlign: 'center', borderRight: '1px solid #E2E8F0', padding: '2px 0' }}
                >
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={Boolean(getTooth(measurements, tooth).absent)}
                    onChange={(e) => updateTooth(tooth, { absent: e.target.checked })}
                    aria-label={`Dente ${tooth} ausente`}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Box>

      <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.5 }}>
        PS = profundidade de sondagem (mm) · MG = margem gengival (mm, negativa indica recessão)
      </Typography>
    </Box>
  );
};

export default PeriodontalChartGrid;
